import { createBrowserRouter, Navigate } from "react-router-dom";

import { LoginPage } from "@/features/auth/login-page";
import { LandingRedirect, RedirectIfAuthed, RequireAuth, RequireSuperAdmin } from "@/features/auth/guards";
import { PortalLayout } from "@/features/portal/portal-layout";
import { BrandRoute } from "@/features/portal/brand-route";
import { CreateBrandPage } from "@/features/portal/create-brand-page";
import { ThemingPage } from "@/features/theming/theming-page";
import { TerminologyPage } from "@/features/terminology/terminology-page";
import { MembersPage } from "@/features/members/members-page";
import { DemoPage } from "@/features/demo/demo-page";
import { AllBrandsPage } from "@/features/admin/all-brands-page";
import { ScenePage } from "@/features/scene/scene-page";
import { SCENE_ENABLED } from "@/config";

export const router = createBrowserRouter([
  // Dev-only living-scene harness (Surface Protocol first impl). Behind the
  // SCENE_ENABLED flag and OUTSIDE the authed portal shell so it can be driven
  // headlessly; makes NO nav/menu IA change. Absent when the flag is off, so the
  // "*" catch-all below redirects `/dev/scene` back to the portal in production.
  ...(SCENE_ENABLED ? [{ path: "/dev/scene", element: <ScenePage /> }] : []),
  {
    path: "/login",
    element: (
      <RedirectIfAuthed>
        <LoginPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/",
    element: <RequireAuth />,
    children: [
      {
        element: <PortalLayout />,
        children: [
          // Pick a landing destination (first brand / create / admin directory).
          { index: true, element: <LandingRedirect /> },
          { path: "create", element: <CreateBrandPage /> },
          // Brand editors. The brand lives in the path and the selected theme in
          // `?theme`; BrandRoute resolves them, loads the draft, and guards
          // unsaved changes. Replaces the old RequireBrand guard.
          {
            path: "b/:brandId",
            element: <BrandRoute />,
            children: [
              { index: true, element: <Navigate to="theming" replace /> },
              { path: "theming", element: <ThemingPage /> },
              { path: "terminology", element: <TerminologyPage /> },
              { path: "members", element: <MembersPage /> },
              { path: "demo", element: <DemoPage /> },
            ],
          },
          // Admin-only "All brands" directory. Outside BrandRoute: a super admin
          // with no brands of their own still needs to reach it.
          {
            element: <RequireSuperAdmin />,
            children: [{ path: "admin/brands", element: <AllBrandsPage /> }],
          },
        ],
      },
    ],
  },
  // Anything else (including legacy section-only URLs like /theming) falls back
  // to the landing redirect via "/".
  { path: "*", element: <Navigate to="/" replace /> },
]);
